import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { MessengerParticipant, MessengerThread } from '../types'

// Selection only. A thread whose conversation doesn't exist yet keeps a null conversationId until the
// first send creates it. See useEnsureConversation.
export const useSelectMessengerThread = (
  threads: MessengerThread[],
  initialParticipant = MessengerParticipant.PATIENT,
  initialConversationId?: string | null,
  // Takes precedence over initialConversationId; targets a thread whose conversation may not exist yet.
  initialThreadId?: string,
) => {
  const clientId = useSelector(getSelectedClientId)
  const normalizedInitialConversationId = initialConversationId ?? null
  const normalizedInitialThreadId = initialThreadId ?? null
  const [requestedThreadId, setRequestedThreadId] = useState<string | null>(null)

  // Re-baseline selection to the caller's requested thread whenever the client or entry-point
  // selection changes, so a selection for one message row doesn't carry over to the next.
  const prevClientIdRef = useRef(clientId)
  const prevInitialParticipantRef = useRef(initialParticipant)
  const prevInitialConversationIdRef = useRef(normalizedInitialConversationId)
  const prevInitialThreadIdRef = useRef(normalizedInitialThreadId)
  useEffect(() => {
    if (
      prevClientIdRef.current !== clientId ||
      prevInitialParticipantRef.current !== initialParticipant ||
      prevInitialConversationIdRef.current !== normalizedInitialConversationId ||
      prevInitialThreadIdRef.current !== normalizedInitialThreadId
    ) {
      prevClientIdRef.current = clientId
      prevInitialParticipantRef.current = initialParticipant
      prevInitialConversationIdRef.current = normalizedInitialConversationId
      prevInitialThreadIdRef.current = normalizedInitialThreadId
      setRequestedThreadId(null)
    }
  }, [clientId, normalizedInitialConversationId, normalizedInitialThreadId, initialParticipant])

  const selectedThread = useMemo(() => {
    if (requestedThreadId) {
      // Retain an explicit target through refetches. Falling back here can load or mark read the
      // patient conversation while an exact AI deep link is still arriving.
      return threads.find((thread) => thread.id === requestedThreadId) ?? null
    }
    if (normalizedInitialThreadId) {
      return threads.find((thread) => thread.id === normalizedInitialThreadId) ?? null
    }
    if (normalizedInitialConversationId) {
      return threads.find((thread) => thread.conversationId === normalizedInitialConversationId) ?? null
    }
    return threads.find((thread) => thread.participant === initialParticipant) ?? threads[0] ?? null
  }, [threads, requestedThreadId, normalizedInitialThreadId, normalizedInitialConversationId, initialParticipant])

  const selectThread = useCallback((threadId: string) => {
    setRequestedThreadId(threadId)
  }, [])

  return {
    selectedThreadId: requestedThreadId ?? selectedThread?.id ?? normalizedInitialThreadId ?? null,
    selectedParticipant: selectedThread?.participant ?? initialParticipant,
    selectedThread,
    selectThread,
    selectedConversationId: selectedThread?.conversationId ?? null,
  }
}
